import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { AnnouncementDetails } from '../../types/announcements/announcements.types';
import { fetchAnnouncementsAPI, updateAnnouncementsAPI } from '../../services/api/announcementsService';
import { AnnouncementsDetailsInput } from 'src/API';

// Define the state interface
interface AnnouncementsState {
  announcements: AnnouncementDetails[];
  selectedAnnouncement: AnnouncementDetails | null;
  isLoading: boolean;
  error: string | null;
  hasAttemptedFetch: boolean;
}

// Initial state
const initialState: AnnouncementsState = {
  announcements: [],
  selectedAnnouncement: null,
  isLoading: false,
  error: null,
  hasAttemptedFetch: false,
};

// Async thunk actions using the helper function for consistent error handling
export const fetchAnnouncements = createAsyncThunk(
  'announcements/fetchAnnouncements',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchAnnouncementsAPI();
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to fetch announcements');
    }
  }
);

export const updateAnnouncements = createAsyncThunk(
  'announcements/updateAnnouncements',
  async (announcement: AnnouncementsDetailsInput, { rejectWithValue }) => {
    try {
      return await updateAnnouncementsAPI([announcement]);
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to update announcements');
    }
  }
);

// Create the slice
const announcementsSlice = createSlice({
  name: 'announcements',
  initialState,
  reducers: {
    setSelectedAnnouncement: (state, action: PayloadAction<AnnouncementDetails | null>) => {
      state.selectedAnnouncement = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    reset: () => initialState,
    resetFetchStatus: (state) => {
      state.hasAttemptedFetch = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchAnnouncements
      .addCase(fetchAnnouncements.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAnnouncements.fulfilled, (state, action) => {
        state.isLoading = false;
        state.announcements = action.payload;
        state.hasAttemptedFetch = true;
      })
      .addCase(fetchAnnouncements.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.hasAttemptedFetch = true;
      });
  },
});

// Export actions
export const { setSelectedAnnouncement, clearError, reset, resetFetchStatus } = announcementsSlice.actions;

// Export selectors
export const selectAnnouncements = (state: RootState) => state.announcements.announcements;
export const selectSelectedAnnouncement = (state: RootState) => state.announcements.selectedAnnouncement;
export const selectIsLoading = (state: RootState) => state.announcements.isLoading;
export const selectError = (state: RootState) => state.announcements.error;
export const selectHasAttemptedFetch = (state: RootState) => state.announcements.hasAttemptedFetch;

// Export reducer
export default announcementsSlice.reducer;
